import {
  inventoryItemIdFormat,
  productIdFormat,
  workOrderIdFormat,
} from "@redotech/redo-ids-cockroach";
import * as z from "zod";

/**
 * Looks up the work order whose `output_inventory_item_id` equals the given
 * inventory item id, and returns enough context for the WMS SKU-lookup
 * surface to show "this unit was produced for order #1001, customer note X,
 * sibling work orders in the same FG are at status Y, and these other line
 * items on the order have no work order at all."
 *
 * Returns null if the inventory item is not the output of any work order
 * (i.e. it's regular stock, not a WO-produced unit).
 */

const WorkOrderProductSchema_ = z.object({
  id: productIdFormat.schema,
  title: z.string().nullable(),
  sku: z.string().nullable(),
  imageUrl: z.string().nullable(),
});

const DisplayAttributeSchema_ = z.object({
  label: z.string(),
  value: z.string().nullable(),
});

const WorkOrderInfoSchema_ = z.object({
  workOrderId: workOrderIdFormat.schema,
  status: z.string(),
  currentStepId: z.string().nullable(),
  lineItemId: z.string().nullable(),
  output: WorkOrderProductSchema_.nullable(),
  customAttributes: z.array(DisplayAttributeSchema_),
});
export type WorkOrderInfo = z.infer<typeof WorkOrderInfoSchema_>;

export const lookupWorkOrderByInventoryItem = {
  name: "lookupWorkOrderByInventoryItem" as const,
  input: z.object({ inventoryItemId: inventoryItemIdFormat.schema }),
  output: z.object({
    /** Null when the inventory item isn't the output of any work order. */
    context: z
      .object({
        workOrder: z.object({
          workOrderId: workOrderIdFormat.schema,
          status: z.string(),
          externalOrderName: z.string().nullable(),
          orderNote: z.string().nullable(),
          lineItemId: z.string().nullable(),
          output: WorkOrderProductSchema_.nullable(),
          /** Resolved (label, value) pairs from the WO flow's
           *  `displayAttributes` config + the FG line item's
           *  `customAttributes`. */
          customAttributes: z.array(DisplayAttributeSchema_),
          /** Bins that currently hold this specific inventory item (not
           *  the general product). */
          bins: z.array(
            z.object({ binName: z.string(), quantity: z.number() }),
          ),
        }),
        /** Other work orders attached to the same fulfillment group. */
        siblings: z.array(WorkOrderInfoSchema_),
        /**
         * Line items on the FG that have no associated work order at all
         * — picked from regular stock, no custom production.
         */
        nonWorkOrderLineItems: z.array(
          z.object({
            lineItemId: z.string(),
            sku: z.string().nullable(),
            title: z.string().nullable(),
            quantity: z.number(),
          }),
        ),
      })
      .nullable(),
  }),
};
